import * as R from 'ramda';
import React from 'react';
import { Formik } from 'formik';
import { Col, Row } from 'react-bootstrap';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { TextField } from '@/shared/form/TextField';
import SelectField from '@/shared/form/SelectField';
import { DatePickerField } from '@/shared/form/DatePickerField';
import FormButtonGroup from 'App/components/styles/FormButtonGroup';
import ResetButton from 'Shared/form/ResetButton';
import SubmitButton from 'Shared/form/SubmitButton';
import dayjs from 'dayjs';
import dayJsUtc from 'dayjs/plugin/utc';
import useGetCustomMenuQuery from 'App/hooks/useGetCustomMenuQuery';
import hasLength from 'App/utils/hasLength';
import TherigyLoadingSpinner from 'App/components/TherigyLoadingSpinner';
import useUpdateReferralMutation from 'App/hooks/useUpdateReferralMutation';
import useGetReferral from './hooks/useGetReferral';
import useGetAvailableReferralMenus from './hooks/useGetAvailableReferralMenus';
import InvalidMessage from 'App/common/styles/InvalidMessage';
import CommonPatientFieldWrapper from 'App/components/PatientForm/components/CommonPatientFieldWrapper';
import { referralManagerRoute } from 'App/common/routeLookup';

dayjs.extend(dayJsUtc);

const propTypes = {};

const defaultProps = {};

const validationSchema = Yup.object().shape({
    updatedOn: Yup.date().required().label('Status Update Date'),
    status: Yup.object().required().nullable().label('Status'),
    statusReason: Yup.object().required().nullable().label('Status Reason'),
    statusSubReason: Yup.object()
        .nullable()
        .label('Status Sub-Reason')
        .when('statusReason', {
            is: (val) => {
                return hasLength(val?.children);
            },
            then: (schema) => schema.required(),
        }),
    statusAction: Yup.object()
        .nullable()
        .label('Status Action')
        .when('statusSubReason', {
            is: (val) => {
                return hasLength(val?.children);
            },
            then: (schema) => schema.required(),
        }),
    statusOutcome: Yup.object()
        .nullable()
        .label('Status Outcome')
        .when('statusAction', {
            is: (val) => {
                return hasLength(val?.children);
            },
            then: (schema) => schema.required(),
        }),

    receivedOn: Yup.date().nullable().label('Received Date'),
    receivedBy: Yup.object().nullable().label('Received From'),

    createdOn: Yup.date().nullable().label('Start Date'),
    number: Yup.string().nullable().label('Referral Number'),

    encounterId: Yup.string().nullable().label('Patient Encounter ID'),
    systemId: Yup.string().nullable().label('Patient Pharmacy System ID'),
    dispenseStatus: Yup.object().nullable().label('Disspense Status'),
    dispenseStatusReason: Yup.object().nullable().label('Disspense Status Reason'),
    hubProgramId: Yup.string().nullable().label('Patient HUB Program ID'),

    statusDate: Yup.date().nullable().label('Status Update Date'),
});

function ReferralInformationForm() {
    const { patientId, referralId } = useParams();
    const navigate = useNavigate();

    const referralUpdateMutation = useUpdateReferralMutation(patientId, referralId);
    const statuses = useGetAvailableReferralMenus();
    const referralQuery = useGetReferral(patientId, referralId, statuses);

    const dispsenseStatusQuery = useGetCustomMenuQuery('referral_dispense_status');
    const dispsenseStatusReasonQuery = useGetCustomMenuQuery('referral_dispense_status_reason');
    const receivedFromQuery = useGetCustomMenuQuery('referral_received_from');

    if (referralQuery.isLoading) {
        return <TherigyLoadingSpinner />;
    }

    return (
        <Formik
            initialValues={referralQuery.referral}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting, resetForm }) => {
                const payload = R.applySpec({
                    status_date: R.propOr(null, 'updatedOn'),
                    created_on: R.propOr(null, 'createdOn'),
                    dispense_status: R.pathOr(null, ['dispenseStatus', 'label']),
                    dispense_status_reason: R.pathOr(null, ['dispenseStatusReason', 'label']),
                    encounter_id: R.propOr(null, 'encounterId'),
                    hub_program_id: R.propOr(null, 'hubProgramId'),
                    no_go_triaged_to: R.always(null),
                    number: R.propOr(null, 'number'),
                    received_by: R.pathOr(null, ['receivedBy', 'label']),
                    received_on: R.propOr(null, 'receivedOn'),
                    status: R.pathOr(null, ['status', 'label']),
                    status_action: R.pathOr(null, ['statusAction', 'label']),
                    status_outcome: R.pathOr(null, ['statusOutcome', 'label']),
                    status_reason: R.pathOr(null, ['statusReason', 'label']),
                    status_sub_reason: R.pathOr(null, ['statusSubReason', 'label']),
                    system_id: R.propOr(null, 'systemId'),
                    type: R.propOr(0, 'type'),
                })(values);

                return referralUpdateMutation
                    .mutateAsync(payload)
                    .then(() => {
                        resetForm({ values });
                        if (payload.status === 'Complete') {
                            navigate(generatePath(referralManagerRoute, { patientId }));
                        }
                    })
                    .catch(() => {
                        setSubmitting(false);
                    });
            }}
        >
            {({ values, setFieldValue, handleSubmit, setValues }) => {
                return (
                    <form className="referral-information-form" noValidate onSubmit={handleSubmit}>
                        <Row className="row" style={{ marginBottom: '0.625rem' }}>
                            <Col md={3}>
                                <DatePickerField
                                    required
                                    label="Status Update Date"
                                    placeholder="Status Update Date"
                                    fieldName="updatedOn"
                                />
                            </Col>

                            <Col md={3}>
                                <SelectField
                                    label="Status"
                                    required
                                    placeholder="Status"
                                    fieldName="status"
                                    options={statuses}
                                    onChange={(option) => {
                                        R.compose(
                                            setValues,
                                            R.set(R.lensProp('status'), option),
                                            R.set(R.lensProp('statusReason'), null),
                                            R.set(R.lensProp('statusSubReason'), null),
                                            R.set(R.lensProp('statusAction'), null),
                                            R.set(R.lensProp('statusOutcome'), null)
                                        )(values);
                                    }}
                                />
                            </Col>
                        </Row>

                        <Row>
                            {R.compose(hasLength, R.path(['status', 'children']))(values) ? (
                                <Col md={3}>
                                    <SelectField
                                        required
                                        label="Status Reason"
                                        fieldName="statusReason"
                                        options={values.status.children}
                                        onChange={(option) => {
                                            R.compose(
                                                setValues,
                                                R.set(R.lensProp('statusReason'), option),
                                                R.set(R.lensProp('statusSubReason'), null),
                                                R.set(R.lensProp('statusAction'), null),
                                                R.set(R.lensProp('statusOutcome'), null)
                                            )(values);
                                        }}
                                    />
                                </Col>
                            ) : (
                                <Col md={12}>
                                    <InvalidMessage>
                                        Unable to fetch all status options. Please try logging out and back in.
                                    </InvalidMessage>
                                </Col>
                            )}

                            {R.compose(hasLength, R.path(['statusReason', 'children']))(values) ? (
                                <Col md={3}>
                                    <SelectField
                                        required
                                        label="Status Sub-Reason"
                                        fieldName="statusSubReason"
                                        options={values.statusReason.children}
                                        onChange={(option) => {
                                            R.compose(
                                                setValues,
                                                R.set(R.lensProp('statusSubReason'), option),
                                                R.set(R.lensProp('statusAction'), null),
                                                R.set(R.lensProp('statusOutcome'), null)
                                            )(values);
                                        }}
                                    />
                                </Col>
                            ) : null}

                            {R.compose(hasLength, R.path(['statusSubReason', 'children']))(values) ? (
                                <Col md={3}>
                                    <SelectField
                                        required
                                        label="Status Action"
                                        fieldName="statusAction"
                                        options={values.statusSubReason.children}
                                        onChange={(option) => {
                                            R.compose(
                                                setValues,
                                                R.set(R.lensProp('statusAction'), option),
                                                R.set(R.lensProp('statusOutcome'), null)
                                            )(values);
                                        }}
                                    />
                                </Col>
                            ) : null}

                            {R.compose(hasLength, R.path(['statusAction', 'children']))(values) ? (
                                <Col md={3}>
                                    <SelectField
                                        required
                                        label="Status Outcome"
                                        fieldName="statusOutcome"
                                        options={values.statusAction.children}
                                    />
                                </Col>
                            ) : null}
                        </Row>

                        <div style={{ height: '3.125rem' }} />

                        <Row>
                            <CommonPatientFieldWrapper fieldName="referralReceivedDate">
                                {(commonProps) => (
                                    <Col md={3}>
                                        <DatePickerField
                                            {...commonProps}
                                            label="Received Date"
                                            fieldName="receivedOn"
                                        />
                                    </Col>
                                )}
                            </CommonPatientFieldWrapper>

                            <CommonPatientFieldWrapper fieldName="referralReceivedFrom">
                                {(commonProps) => (
                                    <Col md={3}>
                                        <SelectField
                                            {...commonProps}
                                            label="Received From"
                                            fieldName="receivedBy"
                                            options={receivedFromQuery.data}
                                        />
                                    </Col>
                                )}
                            </CommonPatientFieldWrapper>
                            <CommonPatientFieldWrapper fieldName="referralStartDate">
                                {(commonProps) => (
                                    <Col md={3}>
                                        <DatePickerField {...commonProps} label="Start Date" fieldName="createdOn" />
                                    </Col>
                                )}
                            </CommonPatientFieldWrapper>
                            <CommonPatientFieldWrapper fieldName="referralReferralNumber">
                                {(commonProps) => (
                                    <Col md={3}>
                                        <TextField
                                            {...commonProps}
                                            fieldName="number"
                                            placeholder="Referral Number"
                                            label="Referral Number"
                                        />
                                    </Col>
                                )}
                            </CommonPatientFieldWrapper>
                        </Row>

                        <Row>
                            <CommonPatientFieldWrapper fieldName="referralPatientEncounterId">
                                {(commonProps) => (
                                    <Col md={3}>
                                        <TextField
                                            {...commonProps}
                                            fieldName="encounterId"
                                            placeholder="Patient Encounter ID"
                                            label="Patient Encounter ID"
                                        />
                                    </Col>
                                )}
                            </CommonPatientFieldWrapper>

                            <CommonPatientFieldWrapper fieldName="referralPatientPharmacySystemId">
                                {(commonProps) => (
                                    <Col md={3}>
                                        <TextField
                                            {...commonProps}
                                            fieldName="systemId"
                                            placeholder="Patient Pharmacy System ID"
                                            label="Patient Pharmacy System ID"
                                        />
                                    </Col>
                                )}
                            </CommonPatientFieldWrapper>

                            <CommonPatientFieldWrapper fieldName="referralDispenseStatus">
                                {(commonProps) => (
                                    <Col md={3}>
                                        <SelectField
                                            {...commonProps}
                                            label="Dispense Status"
                                            placeholder="Dispense Status"
                                            fieldName="dispenseStatus"
                                            options={dispsenseStatusQuery.data}
                                            onChange={(option) => {
                                                setFieldValue('dispenseStatus', option);
                                                setFieldValue('dispenseStatusReason', null);
                                            }}
                                        />
                                    </Col>
                                )}
                            </CommonPatientFieldWrapper>
                            {R.path(['dispenseStatus', 'label'], values) === 'Discontinued' ? (
                                <CommonPatientFieldWrapper fieldName="referralDispenseStatusReason">
                                    {(commonProps) => (
                                        <Col md={3}>
                                            <SelectField
                                                {...commonProps}
                                                label="Dispense Status Reason"
                                                placeholder="Dispense Status Reason"
                                                fieldName="dispenseStatusReason"
                                                options={dispsenseStatusReasonQuery.data}
                                            />
                                        </Col>
                                    )}
                                </CommonPatientFieldWrapper>
                            ) : null}
                        </Row>

                        <Row>
                            <CommonPatientFieldWrapper fieldName="referralPatientHubProgramId">
                                {(commonProps) => (
                                    <Col md={3}>
                                        <TextField
                                            {...commonProps}
                                            label="Patient HUB Program ID"
                                            fieldName="hubProgramId"
                                            placeholder="Patient HUB Program ID"
                                        />
                                    </Col>
                                )}
                            </CommonPatientFieldWrapper>
                        </Row>

                        <FormButtonGroup>
                            <ResetButton>Reset</ResetButton>
                            <SubmitButton>Save</SubmitButton>
                        </FormButtonGroup>
                    </form>
                );
            }}
        </Formik>
    );
}

ReferralInformationForm.propTypes = propTypes;
ReferralInformationForm.defaultProps = defaultProps;

export default ReferralInformationForm;
